/**
 * Active-goal state for the Workflow Card.
 *
 * Mirrors the standalone widget's goalStore.ts in plain JS. The server
 * is the source of truth: every `goal.update` replaces the card's slot
 * snapshot wholesale. Two pieces of widget-side state layer on top:
 *
 *   - active.localState: tracks whether the user has already pressed
 *     Confirm or Cancel locally, so the buttons disable instantly and
 *     governor opacity flips before the server roundtrips back.
 *   - outcome: the most recent commit/dismiss result, kept around for
 *     the card's exit animation. Auto-clears after a short delay so a
 *     stale "appointment booked" toast doesn't linger.
 */

import { useCallback, useEffect, useReducer } from "react";

const INITIAL_STATE = { active: null, outcome: null };

const OUTCOME_TTL_MS = 4_500;

const fromGoalUpdate = (update) => ({
  goalId: update.goal_id,
  title: update.title,
  description: update.description ?? null,
  toolName: update.tool_name,
  classification: update.classification,
  confirmStyle: update.confirm_style,
  slots: update.slots ?? [],
  summary: update.summary ?? null,
  confirmToken: update.confirm_token ?? null,
  expiresAt: update.expires_at ?? null,
  localState: "active",
});

export function goalReducer(state, action) {
  switch (action.type) {
    case "server.update":
      return { active: fromGoalUpdate(action.payload), outcome: null };
    case "server.commit": {
      const payload = action.payload;
      const outcome =
        payload.status === "ok"
          ? {
              kind: "committed",
              goalId: payload.goal_id,
              data: payload.data ?? null,
            }
          : {
              kind: payload.status === "blocked" ? "blocked" : "error",
              goalId: payload.goal_id,
              errorCode: payload.error_code ?? null,
              errorMessage: payload.error_message ?? null,
            };
      const isActive = state.active?.goalId === payload.goal_id;
      return { active: isActive ? null : state.active, outcome };
    }
    case "server.dismiss": {
      const payload = action.payload;
      const isActive = state.active?.goalId === payload.goal_id;
      return {
        active: isActive ? null : state.active,
        outcome: {
          kind: "dismissed",
          goalId: payload.goal_id,
          reason: payload.reason,
          message: payload.message ?? null,
        },
      };
    }
    case "local.confirm":
      if (!state.active) return state;
      if (state.active.localState !== "active") return state;
      return {
        ...state,
        active: { ...state.active, localState: "confirming" },
      };
    case "local.cancel":
      if (!state.active) return state;
      if (state.active.localState !== "active") return state;
      return {
        ...state,
        active: { ...state.active, localState: "cancelling" },
      };
    case "outcome.clear":
      return { ...state, outcome: null };
    default:
      return state;
  }
}

export function useGoalStore() {
  const [state, dispatch] = useReducer(goalReducer, INITIAL_STATE);

  const applyServerEvent = useCallback((event) => {
    if (event.type === "goal.update") {
      dispatch({ type: "server.update", payload: event });
    } else if (event.type === "goal.commit") {
      dispatch({ type: "server.commit", payload: event });
    } else if (event.type === "goal.dismiss") {
      dispatch({ type: "server.dismiss", payload: event });
    }
  }, []);

  const markConfirming = useCallback(
    () => dispatch({ type: "local.confirm" }),
    [],
  );
  const markCancelling = useCallback(
    () => dispatch({ type: "local.cancel" }),
    [],
  );
  const clearOutcome = useCallback(
    () => dispatch({ type: "outcome.clear" }),
    [],
  );

  useEffect(() => {
    if (!state.outcome) return undefined;
    const handle = window.setTimeout(clearOutcome, OUTCOME_TTL_MS);
    return () => window.clearTimeout(handle);
  }, [state.outcome, clearOutcome]);

  return {
    active: state.active,
    outcome: state.outcome,
    applyServerEvent,
    markConfirming,
    markCancelling,
    clearOutcome,
  };
}
